import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { IconComponent, TranslatePipe } from '@softline/ui-core';
import { ensureDocumentsTranslations } from '../documents.i18n';
import { DocumentPreviewComponent } from './document-preview.component';
import { DocumentPreviewRepository } from './document-preview.repository';
import { DocumentPreviewService } from './document-preview.service';
import { pdfBlobFromBase64 } from './pdf-blob.function';

@Component({
  selector: 'soft-document-preview-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DocumentPreviewRepository, useClass: DocumentPreviewRepository },
    DocumentPreviewService,
  ],
  imports: [DocumentPreviewComponent, IconComponent, TranslatePipe],
  templateUrl: './document-preview-panel.component.html',
  styleUrl: './document-preview-panel.component.css',
})
export class DocumentPreviewPanelComponent {
  private readonly previewService = inject(DocumentPreviewService);
  private readonly translationsReady = ensureDocumentsTranslations();

  readonly documentId = input<string | null>(null);

  /** Names the preview in the viewer's meta line and its download. */
  protected readonly fileName = computed(() => {
    const id = this.documentId();
    return id ? `${id}.pdf` : '';
  });

  readonly base64 = this.previewService.base64;
  readonly isLoading = this.previewService.isLoading;
  readonly hasPreview = computed(() => this.base64() !== null);

  constructor() {
    effect(() => {
      const id = this.documentId();
      this.previewService.reset();
      if (id) void this.previewService.loadDocument(id);
    });
  }

  reload(): void {
    const id = this.documentId();
    if (!id) return;
    this.previewService.reset();
    void this.previewService.loadDocument(id);
  }

  openInNewTab(): void {
    const base64 = this.base64();
    if (!base64) return;
    let url: string;
    try {
      url = URL.createObjectURL(pdfBlobFromBase64(base64));
    } catch {
      return;
    }
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}
